import React from "react";
import { Tag, Popover } from "antd";
import * as XLSX from "xlsx";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
} from "../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../components/upload/shared/uploadReport";
import { manpowerApis } from "../../../api/ManpowerNorms";

// Bulk "Apply Manpower" upload — rows are {TaskID, ItemCode, OverrideQty}. Thin
// wrapper over the shared staged-upload modal (upload → validating → preview →
// committing → done). Project-scoped: the current project's id is sent in the
// body so the TaskID (task sequence number) resolves uniquely. Per-row
// resilient — valid rows apply, invalid rows are reported. Repeat a TaskID
// across rows to apply a crew of several PRW items to the same task.

const generateApplyManpowerTemplate = () => {
  const headers = ["TaskID", "ItemCode", "OverrideQty"];
  const sampleRows = [
    ["T-0001", "MAS-01", ""],
    ["T-0001", "HLP-01", ""],
    ["T-0002", "MAS-01", "50"],
  ];
  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
  dataSheet["!cols"] = [{ wch: 18 }, { wch: 18 }, { wch: 14 }];

  const instructions = [
    ["Apply Manpower Upload — Instructions"],
    [""],
    ["TaskID", "Required. The task's number (sequence id) shown in the app, within THIS project. Repeat the TaskID on multiple rows to apply a crew of items to the same task."],
    ["ItemCode", "Required. The PRW manpower item's code. Must be an Active PRW manpower norm (project-scoped, else org master)."],
    ["OverrideQty", "Optional. Replaces the task's work quantity as the basis for that row. Leave blank to use the task work quantity. 0 is allowed; negatives are rejected."],
    [""],
    ["Note", "Applying a manpower item records it as the task's labour requirement (quantity = basis × PUC factor, priced at the unit price). An item can be applied to a task only ONCE — a row whose task already has that item is rejected (remove it with a reason first). Invalid rows (unknown task/item, inactive, already applied, duplicate in file, negative override) are skipped and listed in the preview; valid rows still apply."],
  ];
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionSheet["!cols"] = [{ wch: 16 }, { wch: 110 }];

  const workbook = XLSX.utils.book_new();
  // data sheet MUST be first — the backend parser reads the first worksheet
  XLSX.utils.book_append_sheet(workbook, dataSheet, "ApplyManpower");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");
  XLSX.writeFile(workbook, "apply-manpower-upload-template.xlsx");
};

const rowErrors = (record: any): string[] =>
  Array.isArray(record?.Error)
    ? record.Error
    : record?.Error
    ? [record.Error]
    : [];

const previewColumns: any[] = [
  {
    title: "Status",
    key: "rowStatus",
    width: 100,
    fixed: "left",
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (errors.length) {
        return (
          <Popover
            placement="right"
            title={<span style={{ color: "red" }}>Row Errors</span>}
            content={
              <div style={{ maxWidth: 320 }}>
                {errors.map((err, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    • {err}
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="red">Error</Tag>
          </Popover>
        );
      }
      return record?.status === "Applied" ? (
        <Tag color="blue">Applied</Tag>
      ) : (
        <Tag color="green">Valid</Tag>
      );
    },
  },
  { title: "Task ID", dataIndex: "TaskID", key: "TaskID", width: 120 },
  { title: "Task", dataIndex: "taskName", key: "taskName", width: 200, ellipsis: true },
  { title: "Item Code", dataIndex: "ItemCode", key: "ItemCode", width: 120 },
  { title: "Item", dataIndex: "itemDescription", key: "itemDescription", width: 200, ellipsis: true },
  { title: "Override Qty", dataIndex: "OverrideQty", key: "OverrideQty", width: 110 },
  {
    title: "Error Details",
    key: "errorDetails",
    width: 260,
    render: (_: any, record: any) => {
      const errors = rowErrors(record);
      if (!errors.length) return null;
      return (
        <span style={{ color: "#e85347", fontSize: 12 }}>
          {errors.join("; ")}
        </span>
      );
    },
  },
];

const ApplyManpowerNormUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectId: number | string;
}) => {
  const config: ExcelUploadTypeConfig = React.useMemo(
    () => ({
      title: "Apply Manpower (Excel)",
      entityLabel: "Apply Manpower",
      templateSource: { kind: "client", generate: generateApplyManpowerTemplate },
      startUpload: (formData) => manpowerApis.applyManpowerExcel(formData),
      buildBody: (comments) => ({
        projectId: String(projectId),
        comment: comments ? comments : "N/A",
      }),
      previewColumns,
      rowErrorLabel: (row: any) => (row.TaskID ? `Task ${row.TaskID}` : "Row"),
      copy: {
        uploadIntro: (
          <p>
            Apply PRW manpower items to many tasks at once. Fill the three-column
            sheet (<b>TaskID</b>, <b>ItemCode</b>, <b>OverrideQty</b>) for tasks in
            this project, then upload below. You'll see a per-row preview before
            anything is applied.
          </p>
        ),
        validatingHeading: "Validating your file…",
        validatingSub:
          "Resolving each TaskID and ItemCode. This normally takes a few seconds.",
        committingHeading: "Applying manpower…",
        committingSub:
          "Applying each item to its task. Valid rows apply; invalid rows are skipped.",
        commitCta: "Apply Manpower",
        doneHeading: "Manpower applied",
        doneSub: (report: UploadReportState) => (
          <>
            {report.successCount} row(s) applied
            {report.failureCount ? `, ${report.failureCount} skipped` : ""}. Each
            task's "Manpower from Norm (PRW)" now reflects its crew.
          </>
        ),
        creationFailedNote:
          "Some rows could not be applied — see the errors in the preview.",
      },
      discardOnCancelFromPreview: false,
    }),
    [projectId],
  );

  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={projectId}
    />
  );
};

export default ApplyManpowerNormUploadModal;
